import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Create context that mimics Auth0's context shape
const LocalAuthContext = createContext({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    loginWithRedirect: () => {},
    logout: () => {},
    getAccessTokenSilently: () => Promise.resolve('')
});

export const useLocalAuth = () => useContext(LocalAuthContext);

// Hook that matches useAuth0 interface
export const useAuth0 = () => useContext(LocalAuthContext);

const LocalAuthProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);

    // Check for existing session on mount
    useEffect(() => {
        const checkSession = async () => {
            const storedToken = localStorage.getItem('test_auth_token');
            if (storedToken) {
                try {
                    const response = await fetch('/api/auth/session', {
                        headers: {
                            'Authorization': `Bearer ${storedToken}`
                        }
                    });

                    // Check if response is JSON (API available) or HTML (dev server 404)
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const data = await response.json();
                        if (data.valid) {
                            setToken(storedToken);
                            setIsAuthenticated(true);
                            setUser({
                                sub: 'local|admin',
                                email: 'admin@test.local',
                                name: 'Test Admin',
                                picture: null
                            });
                        } else {
                            localStorage.removeItem('test_auth_token');
                        }
                    } else {
                        // API not available (dev server without backend)
                        // Clear potentially stale token
                        localStorage.removeItem('test_auth_token');
                    }
                } catch (error) {
                    console.error('Session check failed:', error);
                    localStorage.removeItem('test_auth_token');
                }
            }
            setIsLoading(false);
        };
        checkSession();
    }, []);

    // Login function - for local auth, this shows the login form
    // The actual login happens via loginWithCredentials
    const loginWithRedirect = useCallback(() => {
        // In local auth mode, we don't redirect - the login form handles it
        // This is a no-op since AdminLogin will show the form
    }, []);

    // Actual login with credentials
    const loginWithCredentials = useCallback(async (username, password) => {
        setIsLoading(true);
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });
            const data = await response.json();

            if (data.success) {
                localStorage.setItem('test_auth_token', data.token);
                setToken(data.token);
                setIsAuthenticated(true);
                setUser({
                    sub: 'local|admin',
                    email: 'admin@test.local',
                    name: 'Test Admin',
                    picture: null
                });
                return { success: true };
            } else {
                return { success: false, error: data.error || 'Invalid credentials' };
            }
        } catch (error) {
            return { success: false, error: error.message };
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async (options = {}) => {
        const storedToken = localStorage.getItem('test_auth_token');
        if (storedToken) {
            try {
                await fetch('/api/auth/session', {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${storedToken}`
                    }
                });
            } catch (error) {
                console.error('Logout API call failed:', error);
            }
        }
        localStorage.removeItem('test_auth_token');
        setToken(null);
        setIsAuthenticated(false);
        setUser(null);

        // Redirect to the specified URL or default to /admin
        const returnTo = options?.logoutParams?.returnTo || '/admin';
        window.location.href = returnTo;
    }, []);

    const getAccessTokenSilently = useCallback(async () => {
        return token || localStorage.getItem('test_auth_token') || '';
    }, [token]);

    const value = {
        isAuthenticated,
        isLoading,
        user,
        loginWithRedirect,
        loginWithCredentials, // Additional method for local auth
        logout,
        getAccessTokenSilently
    };

    return (
        <LocalAuthContext.Provider value={value}>
            {children}
        </LocalAuthContext.Provider>
    );
};

export default LocalAuthProvider;
